import { useState, useMemo, useEffect, useRef } from 'react';
import {
    normalizeOneQuestion,
    SLIDE_LEVEL_FREE_TEXT_CATEGORY_ID,
    SLIDE_LEVEL_NONE_CATEGORY_ID,
    DEFAULT_FREE_TEXT_OPTION_LABEL,
    DEFAULT_NONE_CATEGORY_LABEL,
} from './slideLevelClassificationModel';
import OrderedListControls from '../../hub/OrderedListControls';
import styles from './ConfigForm.module.css';

let categoryRowKeyCounter = 0;
function nextCategoryRowKey() {
    categoryRowKeyCounter += 1;
    return `slc-cat-row-${categoryRowKeyCounter}`;
}

/**
 * @param {object} props
 * @param {number} props.questionIndex - 0-based display index
 * @param {object} props.question - draft question object
 * @param {(patch: object) => void} props.onChange
 * @param {() => void} props.onRemove
 * @param {boolean} props.canRemove
 * @param {(questionIndex: number) => void} props.onOpenBulkCategories
 * @param {() => void} [props.onMoveUp]
 * @param {() => void} [props.onMoveDown]
 * @param {boolean} [props.canMoveUp]
 * @param {boolean} [props.canMoveDown]
 */
export function QuestionConfigSection({
    questionIndex,
    question,
    onChange,
    onRemove,
    canRemove,
    onOpenBulkCategories,
    onMoveUp,
    onMoveDown,
    canMoveUp = false,
    canMoveDown = false,
}) {
    const normalized = useMemo(
        () => normalizeOneQuestion(question, questionIndex),
        [question, questionIndex],
    );
    const categories = useMemo(
        () => (Array.isArray(question?.categories) ? question.categories : []),
        [question?.categories],
    );
    const allowDiscrete = normalized.allowDiscreteCategories !== false;
    const allowFree = Boolean(normalized.allowFreeText);
    const allowMultiple = Boolean(question?.allowMultiple) && allowDiscrete;
    const answerModeInvalid = !allowDiscrete && !allowFree;
    const freeTextCfg = question?.freeText && typeof question.freeText === 'object'
        ? question.freeText
        : normalized.freeText;
    const freeMultiline = Boolean(freeTextCfg?.multiline);

    const [rowKeys, setRowKeys] = useState(() =>
        categories.map(() => nextCategoryRowKey()),
    );
    const [confirmClearCats, setConfirmClearCats] = useState(false);
    const [draftId, setDraftId] = useState('');
    const [draftLabel, setDraftLabel] = useState('');
    const [draftIdError, setDraftIdError] = useState('');
    const newIdInputRef = useRef(null);

    useEffect(() => {
        setRowKeys((prev) => {
            if (prev.length === categories.length) return prev;
            if (prev.length > categories.length) return prev.slice(0, categories.length);
            const toAdd = categories.length - prev.length;
            return [...prev, ...Array.from({ length: toAdd }, () => nextCategoryRowKey())];
        });
    }, [categories.length]);

    const categoryIdIssuesByIndex = useMemo(() => {
        const counts = new Map();
        const trimmed = categories.map((c) => String(c?.id ?? '').trim());
        trimmed.forEach((id) => {
            if (!id) return;
            counts.set(id, (counts.get(id) ?? 0) + 1);
        });
        const issues = {};
        trimmed.forEach((id, idx) => {
            if (!id) {
                issues[idx] = 'ID is required.';
            } else if (id === SLIDE_LEVEL_FREE_TEXT_CATEGORY_ID) {
                issues[idx] = 'This id is reserved for free-text answers.';
            } else if (id === SLIDE_LEVEL_NONE_CATEGORY_ID) {
                issues[idx] = 'This id is reserved for “none of the above” on multi-select.';
            } else if ((counts.get(id) ?? 0) > 1) {
                issues[idx] = `Duplicate id: "${id}".`;
            }
        });
        return issues;
    }, [categories]);

    const labelPreview = String(question?.label ?? '').trim() || '—';
    const qNum = questionIndex + 1;
    const questionIdPrefix = `slc-q${qNum}`;

    function patch(patch) {
        onChange(patch);
    }

    function patchFreeText(ftPatch) {
        const prev = question?.freeText && typeof question.freeText === 'object'
            ? question.freeText
            : {};
        patch({
            freeText: {
                multiline: false,
                placeholder: '',
                optionLabel: DEFAULT_FREE_TEXT_OPTION_LABEL,
                ...prev,
                ...ftPatch,
            },
        });
    }

    function addCategoryRow() {
        const id = draftId.trim();
        if (!id) {
            setDraftIdError('ID is required.');
            return;
        }
        if (id === SLIDE_LEVEL_FREE_TEXT_CATEGORY_ID) {
            setDraftIdError('This id is reserved for free-text answers.');
            return;
        }
        if (id === SLIDE_LEVEL_NONE_CATEGORY_ID) {
            setDraftIdError('This id is reserved for “none of the above” on multi-select.');
            return;
        }
        if (categories.some((c) => String(c?.id ?? '').trim() === id)) {
            setDraftIdError(`Duplicate id: "${id}".`);
            return;
        }
        setDraftIdError('');
        setRowKeys((prev) => [...prev, nextCategoryRowKey()]);
        patch({
            categories: [...categories, { id, label: draftLabel.trim() || id }],
        });
        setDraftId('');
        setDraftLabel('');
        requestAnimationFrame(() => {
            newIdInputRef.current?.focus?.();
        });
    }

    function removeCategory(i) {
        setRowKeys((prev) => prev.filter((_, j) => j !== i));
        patch({ categories: categories.filter((_, j) => j !== i) });
    }

    function updateCategory(i, catPatch) {
        patch({
            categories: categories.map((c, j) => (j === i ? { ...c, ...catPatch } : c)),
        });
    }

    return (
        <article className={styles.questionCard}>
            <header className={styles.questionCardHeader}>
                <div className={styles.questionCardTitle}>
                    <span className={styles.fieldLabel}>
                        {`Question ${qNum}`}
                    </span>
                    <span className={styles.questionLabelPreview}>
                        {labelPreview}
                    </span>
                    {onMoveUp || onMoveDown ? (
                        <OrderedListControls
                            onMoveUp={onMoveUp}
                            onMoveDown={onMoveDown}
                            canMoveUp={canMoveUp}
                            canMoveDown={canMoveDown}
                        />
                    ) : null}
                </div>
                <button
                    type="button"
                    className={`${styles.button} ${styles.buttonDanger}`}
                    onClick={onRemove}
                    disabled={!canRemove}
                    title={canRemove ? 'Remove this question' : 'At least one question is required'}
                >
                    Remove question
                </button>
            </header>

            <div className={styles.questionCardBody}>
                <div className={styles.fieldGroup}>
                    <label
                        className={styles.fieldLabel}
                        htmlFor={`${questionIdPrefix}-label`}
                    >
                        Question label (optional)
                    </label>
                    <input
                        id={`${questionIdPrefix}-label`}
                        className={styles.input}
                        type="text"
                        value={question?.label ?? ''}
                        placeholder="e.g. Is amyloid present?"
                        onChange={(e) => patch({ label: e.target.value })}
                    />
                    <p className={styles.intro}>
                        Shown with this question in the viewer. Leave empty to use “Question
                        {' '}
                        {qNum}
                        ”.
                    </p>
                </div>

                <div className={styles.fieldGroup}>
                    <span className={styles.fieldLabel}>Answer types</span>
                    <p className={styles.intro}>
                        Choose how annotators answer this question: fixed categories, free-text, or both.
                    </p>
                    <div className={styles.checkboxRow}>
                        <label>
                            <input
                                type="checkbox"
                                checked={allowDiscrete}
                                onChange={(e) => {
                                    const next = e.target.checked;
                                    patch({
                                        allowDiscreteCategories: next,
                                        ...(next ? {} : { allowMultiple: false }),
                                    });
                                }}
                            />
                            Discrete categories
                        </label>
                        <label>
                            <input
                                type="checkbox"
                                checked={allowFree}
                                onChange={(e) => patch({ allowFreeText: e.target.checked })}
                            />
                            Free-text answer
                        </label>
                    </div>
                    {allowDiscrete ? (
                        <div className={styles.checkboxRow}>
                            <label>
                                <input
                                    type="checkbox"
                                    checked={allowMultiple}
                                    onChange={(e) => patch({ allowMultiple: e.target.checked })}
                                />
                                Allow multiple selections (choose all that apply)
                            </label>
                        </div>
                    ) : null}
                    {allowMultiple ? (
                        <p className={styles.intro}>
                            Annotators can select multiple categories. If free-text is enabled,
                            an “Other” row appears in the list (label below) and opens a text
                            editor. “
                            {DEFAULT_NONE_CATEGORY_LABEL}
                            ” is always last so they can mark the question finished when nothing
                            applies.
                        </p>
                    ) : null}
                    {answerModeInvalid ? (
                        <p className={styles.inlineErrorText} role="alert">
                            Select at least one answer type.
                        </p>
                    ) : null}

                    {allowFree ? (
                        <div className={styles.freeTextConfigSection}>
                            <label
                                className={styles.fieldLabel}
                                htmlFor={`${questionIdPrefix}-ft-option-label`}
                            >
                                {allowMultiple
                                    ? 'Other row label (shown in the multi-select choice list)'
                                    : 'Free-text option label (shown in dropdown when categories are also enabled)'}
                            </label>
                            <input
                                id={`${questionIdPrefix}-ft-option-label`}
                                className={styles.input}
                                type="text"
                                value={freeTextCfg?.optionLabel ?? ''}
                                placeholder={`Leave empty for default: ${DEFAULT_FREE_TEXT_OPTION_LABEL}`}
                                onChange={(e) => patchFreeText({ optionLabel: e.target.value })}
                            />
                            <label
                                className={styles.fieldLabel}
                                htmlFor={`${questionIdPrefix}-ft-placeholder`}
                            >
                                Placeholder for annotator input
                            </label>
                            <input
                                id={`${questionIdPrefix}-ft-placeholder`}
                                className={styles.input}
                                type="text"
                                value={freeTextCfg?.placeholder ?? ''}
                                placeholder="e.g. Type your answer here"
                                onChange={(e) => patchFreeText({ placeholder: e.target.value })}
                            />
                            <span className={styles.fieldLabel}>Input shape</span>
                            <div className={styles.checkboxRow}>
                                <label>
                                    <input
                                        type="radio"
                                        name={`${questionIdPrefix}-ft-shape`}
                                        checked={!freeMultiline}
                                        onChange={() => patchFreeText({ multiline: false })}
                                    />
                                    Single line (toolbar or popover)
                                </label>
                                <label>
                                    <input
                                        type="radio"
                                        name={`${questionIdPrefix}-ft-shape`}
                                        checked={freeMultiline}
                                        onChange={() => patchFreeText({ multiline: true })}
                                    />
                                    Multiline (popover)
                                </label>
                            </div>
                        </div>
                    ) : null}
                </div>

                {allowDiscrete ? (
                    <div className={styles.fieldGroup}>
                        <div className={styles.categoriesHeader}>
                            <span className={styles.fieldLabel}>Categories</span>
                        </div>
                        <p className={styles.intro}>
                            Classes shown in the viewer for this question.
                        </p>
                        <div className={styles.btnRow}>
                            <button
                                type="button"
                                className={styles.button}
                                onClick={() => onOpenBulkCategories(questionIndex)}
                            >
                                Paste or import categories…
                            </button>
                            <button
                                type="button"
                                className={`${styles.button} ${styles.buttonDanger}`}
                                onClick={() => {
                                    if (!confirmClearCats) {
                                        setConfirmClearCats(true);
                                        return;
                                    }
                                    setRowKeys([]);
                                    patch({ categories: [] });
                                    setConfirmClearCats(false);
                                }}
                            >
                                {confirmClearCats
                                    ? 'Click again to clear all categories'
                                    : 'Clear all categories'}
                            </button>
                        </div>

                        {categories.length === 0 ? (
                            <p className={styles.intro}>No categories yet.</p>
                        ) : (
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Label</th>
                                        <th aria-label="Remove" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {categories.map((c, i) => (
                                        <tr key={rowKeys[i] || `slc-cat-fallback-${qNum}-${i}`}>
                                            <td>
                                                <input
                                                    className={[
                                                        styles.input,
                                                        categoryIdIssuesByIndex[i]
                                                            ? styles.inputError
                                                            : '',
                                                    ].filter(Boolean).join(' ')}
                                                    value={c.id}
                                                    aria-invalid={Boolean(
                                                        categoryIdIssuesByIndex[i],
                                                    )}
                                                    aria-describedby={
                                                        categoryIdIssuesByIndex[i]
                                                            ? `slc-cat-id-error-${rowKeys[i] || `slc-cat-fallback-${qNum}-${i}`}`
                                                            : undefined
                                                    }
                                                    onChange={(e) => updateCategory(i, {
                                                        id: e.target.value,
                                                    })}
                                                    aria-label={`Question ${qNum} category ${i + 1} id`}
                                                />
                                                {categoryIdIssuesByIndex[i] ? (
                                                    <div className={styles.inlineErrorRow}>
                                                        <span
                                                            id={`slc-cat-id-error-${rowKeys[i] || `slc-cat-fallback-${qNum}-${i}`}`}
                                                            className={styles.inlineErrorText}
                                                            role="alert"
                                                        >
                                                            {categoryIdIssuesByIndex[i]}
                                                        </span>
                                                    </div>
                                                ) : null}
                                            </td>
                                            <td>
                                                <input
                                                    className={styles.input}
                                                    value={c.label ?? ''}
                                                    onChange={(e) => updateCategory(i, {
                                                        label: e.target.value,
                                                    })}
                                                    aria-label={`Question ${qNum} category ${i + 1} label`}
                                                />
                                            </td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className={`${styles.button} ${styles.buttonDanger}`}
                                                    onClick={() => removeCategory(i)}
                                                >
                                                    Remove
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}

                        <div className={styles.inlineRow}>
                            <input
                                ref={newIdInputRef}
                                className={[
                                    styles.input,
                                    draftIdError ? styles.inputError : '',
                                ].filter(Boolean).join(' ')}
                                placeholder="New id"
                                value={draftId}
                                aria-invalid={Boolean(draftIdError)}
                                aria-describedby={
                                    draftIdError ? `${questionIdPrefix}-new-cat-id-error` : undefined
                                }
                                onChange={(e) => {
                                    const next = e.target.value;
                                    setDraftId(next);
                                    const trimmedId = String(next ?? '').trim();
                                    if (!trimmedId) {
                                        setDraftIdError('');
                                        return;
                                    }
                                    if (trimmedId === SLIDE_LEVEL_FREE_TEXT_CATEGORY_ID) {
                                        setDraftIdError('This id is reserved for free-text answers.');
                                        return;
                                    }
                                    if (trimmedId === SLIDE_LEVEL_NONE_CATEGORY_ID) {
                                        setDraftIdError(
                                            'This id is reserved for “none of the above” on multi-select.',
                                        );
                                        return;
                                    }
                                    if (categories.some(
                                        (c) => String(c?.id ?? '').trim() === trimmedId,
                                    )) {
                                        setDraftIdError(`Duplicate id: "${trimmedId}".`);
                                        return;
                                    }
                                    setDraftIdError('');
                                }}
                                aria-label={`Question ${qNum} new category id`}
                            />
                            <input
                                className={styles.input}
                                placeholder="Label (optional)"
                                value={draftLabel}
                                onChange={(e) => setDraftLabel(e.target.value)}
                                aria-label={`Question ${qNum} new category label`}
                            />
                            <button
                                type="button"
                                className={styles.button}
                                onClick={addCategoryRow}
                                disabled={Boolean(draftIdError) || !String(draftId ?? '').trim()}
                                title={draftIdError || undefined}
                            >
                                Add row
                            </button>
                        </div>
                        {draftIdError ? (
                            <div className={styles.inlineErrorRow}>
                                <span
                                    id={`${questionIdPrefix}-new-cat-id-error`}
                                    className={styles.inlineErrorText}
                                    role="alert"
                                >
                                    {draftIdError}
                                </span>
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </div>
        </article>
    );
}
